import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import CopyButton from "~/components/CopyButton";
import useRefresh from "~/components/filters/useRefresh";
import { useSnackbar } from "~/components/snackbar";
import { adminLyricsReportsService } from "~/connectors/lyricsReports.connector";
import type { LyricsReport } from "~/types";
import { reportToClipboard } from "./reportToClipboard";

type Props = {
  report: LyricsReport;
  onRefresh?: () => void | Promise<void>;
};

export default function ReportRowActions({ report, onRefresh }: Props) {
  const { refresh, isRefreshing } = useRefresh();
  const { showSuccess, showError } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleMarkResolved = async () => {
    refresh(async () => {
      try {
        await adminLyricsReportsService.updateStatus(report.id, "resolved");
        showSuccess("Marcat ca rezolvat");
        await onRefresh?.();
      } catch (err) {
        showError(err instanceof Error ? err.message : "Eroare la actualizare");
      }
    });
  };

  const handleUndo = async () => {
    refresh(async () => {
      try {
        await adminLyricsReportsService.updateStatus(report.id, "new");
        showSuccess("Status resetat la Nou");
        await onRefresh?.();
      } catch (err) {
        showError(err instanceof Error ? err.message : "Eroare la actualizare");
      }
    });
  };

  const handleDelete = async () => {
    refresh(async () => {
      try {
        await adminLyricsReportsService.deleteReport(report.id);
        showSuccess("Raport șters");
        setDeleteDialogOpen(false);
        await onRefresh?.();
      } catch (err) {
        showError(err instanceof Error ? err.message : "Eroare la ștergere");
      }
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <CopyButton title="Copiază raportul" value={reportToClipboard(report)} />

        {report.status !== "resolved" ? (
          <Tooltip title="Marchează ca rezolvat">
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={handleMarkResolved}
                disabled={isRefreshing}
                aria-label="Marchează ca rezolvat"
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Anulează rezolvarea (resetează la Nou)">
            <span>
              <IconButton
                size="small"
                color="default"
                onClick={handleUndo}
                disabled={isRefreshing}
                aria-label="Anulează rezolvarea"
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Șterge raportul">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isRefreshing}
              aria-label="Șterge raportul"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Șterge raportul</DialogTitle>
        <DialogContent>
          <Typography>
            Ești sigur că vrei să ștergi raportul #{report.id}? Această acțiune
            este ireversibilă.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anulează</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isRefreshing}
          >
            Șterge
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
